import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

export function LanguageSelector({ currentLanguage, onLanguageChange }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'nl', label: 'Nederlands', shortLabel: 'NL', color: '#F8BD3D' },
    { code: 'fr', label: 'Français', shortLabel: 'FR', color: '#F8BD3D' },
    { code: 'en', label: 'English', shortLabel: 'EN', color: '#F8BD3D' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[1];

  const handleLanguageChange = (code: string) => {
    onLanguageChange(code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-black/5 transition-colors"
      >
        {/* Mobile: afficher uniquement la langue active */}
        <div className="sm:hidden">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-black"
            style={{ backgroundColor: currentLang.color }}
          >
            {currentLang.shortLabel}
          </div>
        </div>

        {/* Desktop: afficher tous les ronds */}
        <div className="hidden sm:flex items-center gap-3">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                currentLanguage === lang.code
                  ? 'text-black scale-110'
                  : 'text-black/40 scale-90'
              }`}
              style={{
                width: '35px',
                height: '35px',
                backgroundColor: currentLanguage === lang.code ? lang.color : 'transparent',
                border: currentLanguage === lang.code ? 'none' : '2px solid rgba(0,0,0,0.1)'
              }}
            >
              {lang.shortLabel}
            </div>
          ))}
        </div>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-lg shadow-xl border border-neutral-200 z-50">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors flex items-center gap-3 ${
                  currentLanguage === language.code ? 'bg-neutral-50' : ''
                }`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black"
                  style={{ backgroundColor: language.color }}
                >
                  {language.shortLabel}
                </div>
                <span className="text-sm font-medium text-black">{language.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
